import type { LoadingState } from '@infinex/types';

import type {
  ExternalSigningDeviceForList,
  ExternalWalletForList,
  MultiSigWalletForList,
  PrivateKeyForList,
  RecoveryPhraseForList,
  VaultForList,
  WalletForList,
} from './useCustodiesList';
import { useCustodiesList } from './useCustodiesList';

import type { UnifiedBalances } from '../../../features/balances';
import {
  sumBalanceUsd,
  useUnifiedBalances,
  useUsdBalanceTotals,
} from '../../../features/balances';

type RecoveryPhraseWithBalanceForList = Omit<
  RecoveryPhraseForList,
  'wallets'
> & {
  wallets: WalletWithBalanceForList[];
};

type PrivateKeyWithBalanceForList = Omit<PrivateKeyForList, 'wallet'> & {
  wallet: WalletWithBalanceForList;
};

type ExternalSigningDeviceWithBalanceForList = Omit<
  ExternalSigningDeviceForList,
  'wallets'
> & {
  wallets: ExternalWalletWithBalanceForList[];
};

export type WalletWithBalanceForList = WalletForList & {
  totalUsdBalance: LoadingState<number>;
  totalUsdTokenOnlyBalance: LoadingState<number>;
};

export type ExternalWalletWithBalanceForList = ExternalWalletForList & {
  totalUsdBalance: LoadingState<number>;
  totalUsdTokenOnlyBalance: LoadingState<number>;
};

export type MultiSigWalletWithBalanceForList = MultiSigWalletForList & {
  totalUsdBalance: LoadingState<number>;
  totalUsdTokenOnlyBalance: LoadingState<number>;
};

export type VaultWithBalanceForList = VaultForList & {
  totalUsdBalance: LoadingState<number>;
  totalUsdTokenOnlyBalance: LoadingState<number>;
};

export type CustodyWithBalanceForList =
  | RecoveryPhraseWithBalanceForList
  | PrivateKeyWithBalanceForList
  | ExternalSigningDeviceWithBalanceForList
  | MultiSigWalletWithBalanceForList
  | VaultWithBalanceForList;

type WalletLike = {
  chainRuntimeAddresses: Record<string, { address: string }>;
};

/**
 * Calculates USD balances for a wallet based on its chain runtime addresses.
 */
function calculateWalletBalances<T extends WalletLike>(
  wallet: T,
  balances: UnifiedBalances
): T & {
  totalUsdBalance: LoadingState<number>;
  totalUsdTokenOnlyBalance: LoadingState<number>;
} {
  if (balances.isLoading) {
    return {
      ...wallet,
      totalUsdBalance: { isLoading: true, value: undefined },
      totalUsdTokenOnlyBalance: { isLoading: true, value: undefined },
    };
  }

  const chainRuntimeAddresses = Object.values(wallet.chainRuntimeAddresses);
  const uniqueAddresses = [
    ...new Set(chainRuntimeAddresses.map((cra) => cra.address)),
  ];

  const { totalUsdBalance, totalUsdTokenOnlyBalance } = uniqueAddresses.reduce(
    (acc, address) => {
      const balancesForAddress = balances.balances.filter(
        (balance) => balance.walletAddress === address
      );
      const tokenBalancesForAddress = balancesForAddress.filter(
        (balance) => balance.type === 'token'
      );

      const { totalUsd: addressUsdBalance } = sumBalanceUsd(balancesForAddress);
      const { totalUsd: addressTokenOnlyUsdBalance } = sumBalanceUsd(
        tokenBalancesForAddress
      );

      return {
        totalUsdBalance: acc.totalUsdBalance + addressUsdBalance,
        totalUsdTokenOnlyBalance:
          acc.totalUsdTokenOnlyBalance + addressTokenOnlyUsdBalance,
      };
    },
    { totalUsdBalance: 0, totalUsdTokenOnlyBalance: 0 }
  );

  return {
    ...wallet,
    totalUsdBalance: { isLoading: false, value: totalUsdBalance },
    totalUsdTokenOnlyBalance: {
      isLoading: false,
      value: totalUsdTokenOnlyBalance,
    },
  };
}

/**
 * Returns list of custodies (wallets + vaults) with their USD balances.
 * Design/UI-driven - Used for displaying custody lists in the UI (portfolio, move dialog, etc.)
 *
 * See `useCustodies` for custodies **without** USD balances. Please keep changes in sync with both
 *
 * @param options.excludeVaults - If true, only returns wallets (useful for e.g. swidge where vaults are invalid)
 * @returns Array of custodies with balance information
 */
export function useCustodiesListWithBalance(options: {
  excludeVaults: boolean;
}): LoadingState<CustodyWithBalanceForList[]> {
  const { value: custodiesList, isLoading: isCustodiesListLoading } =
    useCustodiesList(options);

  // Get balances for wallets (exclude legacy-wallet)
  // Include external-signer and multi-sig custody otherwise no balances will show
  const allWalletBalances = useUnifiedBalances({
    custody: ['wallet', 'external-signer', 'multi-sig'],
  });

  // Get balances for vaults if not excluded
  const vaultBalances = useUnifiedBalances(
    options.excludeVaults ? undefined : { custody: ['vault'] }
  );
  const vaultUsdTotals = useUsdBalanceTotals(
    options.excludeVaults ? undefined : { custody: ['vault'] }
  );

  if (
    isCustodiesListLoading ||
    allWalletBalances.isLoading ||
    vaultBalances.isLoading ||
    vaultUsdTotals.isLoading
  ) {
    return { isLoading: true, value: undefined };
  }

  const custodiesWithBalance: CustodyWithBalanceForList[] = custodiesList.map(
    (custody) => {
      // Seed phrases have wallets array
      if (custody.groupKind === 'recoveryPhrase') {
        return {
          ...custody,
          wallets: custody.wallets.map((wallet) =>
            calculateWalletBalances(wallet, allWalletBalances)
          ),
        };
      }

      // External signing devices have wallets array (same as seed phrases)
      if (custody.groupKind === 'externalSigningDevice') {
        return {
          ...custody,
          wallets: custody.wallets.map((wallet) =>
            calculateWalletBalances(wallet, allWalletBalances)
          ),
        };
      }

      // Multi-sig wallet (flat: the entry IS the wallet)
      if (custody.groupKind === 'multiSigWallet') {
        return calculateWalletBalances(custody, allWalletBalances);
      }

      // Private key has single wallet
      if (custody.groupKind === 'privateKey') {
        return {
          ...custody,
          wallet: calculateWalletBalances(custody.wallet, allWalletBalances),
        };
      }

      // Vault
      if (vaultUsdTotals.isLoading) {
        return {
          ...custody,
          totalUsdBalance: { isLoading: true, value: undefined },
          totalUsdTokenOnlyBalance: { isLoading: true, value: undefined },
        };
      }
      return {
        ...custody,
        totalUsdBalance: {
          isLoading: false,
          value: vaultUsdTotals.totalUsdBalance,
        },
        totalUsdTokenOnlyBalance: {
          isLoading: false,
          value: vaultUsdTotals.totalUsdTokenBalance,
        },
      };
    }
  );

  return { isLoading: false, value: custodiesWithBalance };
}
